import { HideObjectType } from '../../../../@types/enums';
import { AiLayoutVariant } from '../../../../@types/store';
import { categoryLayoutVariantDefs, filterRoofIdsBySide } from '../../../../services/layoutVariantPrompts';
import { useViewerStore } from '../../../../store/store';
import { SetTopFitView } from '../Three-JS-Viewer/Controllers/CameraControl';
import {
    addModulesToRoofsByIds,
    removeGreyModulesAndSingleModuleRoofs,
} from '../Three-JS-Viewer/Controllers/ModuleControl';
import { syncPathwayMeshColorsFromPathActive } from '../Three-JS-Viewer/Controllers/PathwayControl';
import {
    DEFAULT_LAYOUT_VARIANT_GENERATION_OPTIONS,
    type LayoutVariantGenerationOptions,
} from './aiRunPayloadSelection';
import { beginBatchLayoutSiteCalc, endBatchLayoutSiteCalc, awaitAuthoritativeModuleGraph } from './utils/layout';
import { captureLiveLayoutCardMetrics } from '../../../../helpers/aiDesignProductionSync';

type RunGenerateLayoutVariantsActionArgs = {
    sceneViewer: any;
    defaultMaxfillRoofIds: string[];
    bestRoofsForLayout: any;
    restoreDefaultLayoutFromSnapshot: () => Promise<void>;
    setAiLoadingStep: (next: string) => void;
    layoutVariantOptions?: LayoutVariantGenerationOptions;
};

export async function runGenerateLayoutVariantsAction(
    args: RunGenerateLayoutVariantsActionArgs,
): Promise<string> {
    const {
        sceneViewer,
        defaultMaxfillRoofIds,
        bestRoofsForLayout,
        restoreDefaultLayoutFromSnapshot,
        setAiLoadingStep,
        layoutVariantOptions,
    } = args;

    const genOpts: Required<LayoutVariantGenerationOptions> = {
        ...DEFAULT_LAYOUT_VARIANT_GENERATION_OPTIONS,
        ...layoutVariantOptions,
    };

    setAiLoadingStep('Trying layout variants (portrait, landscape, efficiency)...');
    let layoutVariantsSummary = '';
    if (!sceneViewer?.roofMeshArr?.length) return layoutVariantsSummary;

    const allIds = sceneViewer.roofMeshArr.map((r: any) => r.roofFaceId).filter(Boolean);
    if (allIds.length <= 0) return layoutVariantsSummary;

    beginBatchLayoutSiteCalc();
    try {
    const viewerState: any = useViewerStore.getState();
    const baseScenario = viewerState.aiModuleFillingScenarios?.[0];
    const shadeWeights = baseScenario?.shadeWeights ?? [];
    type RoofScore = { roofFaceId: string; roofNum: number | null; productionApprox: number };
    const scores: RoofScore[] = [];
    if (shadeWeights.length > 0) {
        const totalWattage = typeof baseScenario?.actualWattage === 'number' ? baseScenario.actualWattage : 0;
        const sumMult = shadeWeights.reduce((s: number, r: any) => s + (r.roofMultiplier ?? 0), 0);
        const sumModuleWeight = shadeWeights.reduce(
            (s: number, r: any) => s + (r.moduleCount ?? 0) * (r.roofMultiplier ?? 0),
            0,
        );
        shadeWeights.forEach((r: any) => {
            const moduleCount = r.moduleCount ?? 0;
            const shareFormula =
                sumModuleWeight > 0 && totalWattage
                    ? ((moduleCount * (r.roofMultiplier ?? 0)) / sumModuleWeight) * totalWattage
                    : sumMult > 0
                        ? ((r.roofMultiplier ?? 0) / sumMult) * totalWattage
                        : 0;
            const productionApprox = Math.round(
                r.actualProduction != null && Number.isFinite(r.actualProduction) ? r.actualProduction : shareFormula,
            );
            scores.push({
                roofFaceId: r.roofFaceId,
                roofNum: typeof r.roofNum === 'number' && Number.isFinite(r.roofNum) ? r.roofNum : null,
                productionApprox,
            });
        });
        scores.sort((a, b) => b.productionApprox - a.productionApprox);
    }

    const maxBestRoofs = 3;
    const bestRoofIds = scores
        .slice(0, maxBestRoofs)
        .map((s) => s.roofFaceId)
        .filter((id) => typeof id === 'string' && id);

    const roofNumById = new Map<string, number | null>();
    scores.forEach((s) => {
        roofNumById.set(s.roofFaceId, s.roofNum);
    });

    const describeRoofSubset = (ids: string[]): string => {
        const numsOrIds = ids.map((id) => {
            const num = roofNumById.get(id);
            if (typeof num === 'number' && Number.isFinite(num)) return `Roof ${num}`;
            const idx = allIds.indexOf(id);
            return idx >= 0 ? `Roof ${idx + 1}` : id;
        });
        return numsOrIds.join(', ');
    };

    type Variant = { label: string; dir: 'port' | 'land' | 'both' | null; minEff: number; roofIds: string[] };
    const globalAllRoofsVariants: Variant[] = [
        { label: 'All roofs - portrait only (60% min efficiency)', dir: 'port', minEff: 60, roofIds: allIds },
        { label: 'All roofs - landscape only (60% min efficiency)', dir: 'land', minEff: 60, roofIds: allIds },
        { label: 'All roofs - mixed orientations (60% min efficiency)', dir: 'both', minEff: 60, roofIds: allIds },
        {
            label: 'All roofs - mixed orientations (50% min efficiency, Balanced)',
            dir: 'both',
            minEff: 50,
            roofIds: allIds,
        },
    ];

    const globalBestSubsetVariants: Variant[] = [];
    if (bestRoofIds.length > 0) {
        const anchor = bestRoofIds[0];
        const others = bestRoofIds.slice(1, 2);
        const subsets: string[][] = [[anchor]];
        if (others[0]) subsets.push([anchor, others[0]]);
        subsets.forEach((subset) => {
            const subsetLabel = describeRoofSubset(subset);
            globalBestSubsetVariants.push(
                { label: `${subsetLabel} - all portrait (60% min efficiency)`, dir: 'port', minEff: 60, roofIds: subset },
                {
                    label: `${subsetLabel} - all landscape (60% min efficiency)`,
                    dir: 'land',
                    minEff: 60,
                    roofIds: subset,
                },
                {
                    label: `${subsetLabel} - mixed orientations (60% min efficiency)`,
                    dir: 'both',
                    minEff: 60,
                    roofIds: subset,
                },
            );
        });
    }

    const variants: Variant[] = [];
    if (genOpts.includeGlobalAllRoofs) variants.push(...globalAllRoofsVariants);
    if (genOpts.includeGlobalBestSubsets) variants.push(...globalBestSubsetVariants);

    const lines: string[] = [];
    const variantsForStore: AiLayoutVariant[] = [];

    const snapshotPathwayActiveById: Record<string, boolean> = {};
    if (sceneViewer.roofGroup) {
        sceneViewer.roofGroup.traverse((child: any) => {
            if (child.meshType === 'pathway' && child.pathwayId) {
                snapshotPathwayActiveById[child.pathwayId] = !!child.pathActive;
            }
        });
    }

    type PathwayModeKey = 'base' | 'altA' | 'altB';
    const pathwayModes: PathwayModeKey[] = ['base'];
    const activePathIds = Object.keys(snapshotPathwayActiveById).filter((id) => snapshotPathwayActiveById[id]);

    const applyPathwayMode = (mode: PathwayModeKey) => {
        if (!sceneViewer.roofGroup || !sceneViewer.roofs?.length) return;
        const desiredActiveById: Record<string, boolean> = {};
        if (mode === 'base') {
            Object.keys(snapshotPathwayActiveById).forEach((id) => {
                desiredActiveById[id] = snapshotPathwayActiveById[id];
            });
        } else {
            let idx = 0;
            activePathIds.forEach((id) => {
                desiredActiveById[id] = mode === 'altA' ? idx % 2 === 0 : idx % 2 === 1;
                idx += 1;
            });
            Object.keys(snapshotPathwayActiveById).forEach((id) => {
                if (!(id in desiredActiveById)) desiredActiveById[id] = false;
            });
        }

        const faces = sceneViewer.roofs?.[0]?.faces ?? [];
        faces.forEach((face: any) => {
            face.lines?.forEach((line: any) => {
                if (!line || !line.lineId) return;
                if (Object.prototype.hasOwnProperty.call(desiredActiveById, line.lineId)) {
                    line.active = desiredActiveById[line.lineId];
                }
            });
        });

        sceneViewer.roofGroup.traverse((child: any) => {
            if (child.meshType === 'pathway' && child.pathwayId) {
                const desired = desiredActiveById[child.pathwayId];
                if (typeof desired === 'boolean') child.pathActive = desired;
            }
        });
        syncPathwayMeshColorsFromPathActive(sceneViewer);
    };

    try {
        sceneViewer.setVisibleGroup?.('tree', false);
        SetTopFitView(sceneViewer as any, {
            includeRoofs: true,
            includeTrees: false,
            includeObstructions: false,
            includeMapCustom: false,
            padding: 1.15,
            minZoom: 1.1,
            maxZoom: 22,
            height: 105,
        });
    } catch {
        // ignore camera issues
    }

    for (const v of variants) {
        for (const mode of pathwayModes) {
            applyPathwayMode(mode);
            addModulesToRoofsByIds(sceneViewer, v.roofIds, v.dir, { skipShadePrewarm: true });
            await awaitAuthoritativeModuleGraph(sceneViewer);
            removeGreyModulesAndSingleModuleRoofs(sceneViewer, v.roofIds, v.minEff);
            await awaitAuthoritativeModuleGraph(sceneViewer);

            const live = captureLiveLayoutCardMetrics();
            const w = live.production;
            const m = live.modules;
            const eff = live.efficiency;
            const solar = live.solarAccess;
            const mult = live.multiplier;

            const modeLabelSuffix =
                mode === 'base' ? '' : mode === 'altA' ? ' - alternative pathways A' : ' - alternative pathways B';
            const labelWithPathways = `${v.label}${modeLabelSuffix}`;
            lines.push(
                `${labelWithPathways}: ${Math.round(w).toLocaleString()} W, ${m} modules${
                    eff != null ? `, ${eff.toFixed(1)}% efficiency` : ''
                }${solar != null ? `, ${solar.toFixed(1)}% solar access` : ''}`,
            );

            const variantId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            let snapshotUrl: string | undefined;
            try {
                const img = (sceneViewer as any)?.getSquareImageOfSite?.();
                if (img && typeof img === 'string') snapshotUrl = img;
            } catch {
                snapshotUrl = undefined;
            }

            variantsForStore.push({
                id: variantId,
                label: labelWithPathways,
                roofIds: v.roofIds,
                dir: v.dir,
                minEff: v.minEff,
                productionW: w,
                modules: m,
                efficiency: eff,
                solarAccess: solar,
                multiplier: mult,
                snapshotUrl,
            });

            if (snapshotUrl) {
                const snapshotKey = JSON.stringify({
                    label: labelWithPathways,
                    roofIds: v.roofIds || [],
                    dir: v.dir ?? null,
                });
                useViewerStore.setState((prev: any) => ({
                    aiLayoutVariantSnapshots: {
                        ...(prev.aiLayoutVariantSnapshots || {}),
                        [snapshotKey]: snapshotUrl,
                    },
                }));
            }

            await new Promise((r) => setTimeout(r, 200));
        }
    }

    applyPathwayMode('base');
    try {
        await restoreDefaultLayoutFromSnapshot();
        sceneViewer.setVisibleGroup?.('maps', true);
        if ((sceneViewer as any).mapCustom) (sceneViewer as any).mapCustom.visible = true;
        sceneViewer.setVisibleGroup?.('tree', true);
        sceneViewer.hideObject?.(HideObjectType.GhostTree, (useViewerStore.getState() as any).isHideAllGhostTrees);
        SetTopFitView(sceneViewer as any, {
            includeRoofs: true,
            includeTrees: true,
            includeObstructions: true,
            includeMapCustom: true,
            padding: 1.25,
            minZoom: 0.35,
            maxZoom: 12,
            height: 130,
        });
    } catch {
        // continue with current layout if restore fails
    }

    layoutVariantsSummary = lines.join('\n');
    try {
        useViewerStore.setState({ aiModuleFillingLayoutVariants: variantsForStore } as any);
    } catch {
        // ignore store sync errors
    }

    const defaultIds = defaultMaxfillRoofIds.length > 0 ? defaultMaxfillRoofIds : (bestRoofsForLayout?.roofFaceIds ?? []);
    if (defaultIds.length > 0 && genOpts.includeCategoryLayoutCards) {
        const allRoofIds: string[] = (sceneViewer.roofMeshArr ?? []).map((r: any) => r.roofFaceId).filter(Boolean);
        const categoryRoofIdsMap: Record<string, string[]> = {};
        Object.keys(categoryLayoutVariantDefs).forEach((category) => {
            categoryRoofIdsMap[category] = defaultIds;
        });

        const nextCategoryVariants: Record<string, any[]> = {};
        for (const [categoryLabel, roofFaceIds] of Object.entries(categoryRoofIdsMap)) {
            const defs = categoryLayoutVariantDefs[categoryLabel] || [];
            const categoryVariants: any[] = [];
            for (const def of defs) {
                if (!def) continue;
                const effectiveIds = def.side ? filterRoofIdsBySide(sceneViewer.roofMeshArr, allRoofIds, def.side) : roofFaceIds;
                addModulesToRoofsByIds(sceneViewer, effectiveIds, def.dir, { skipShadePrewarm: true });
                await awaitAuthoritativeModuleGraph(sceneViewer);
                if (def.minEff != null) {
                    removeGreyModulesAndSingleModuleRoofs(sceneViewer, effectiveIds, def.minEff);
                } else {
                    removeGreyModulesAndSingleModuleRoofs(sceneViewer, effectiveIds);
                }
                await awaitAuthoritativeModuleGraph(sceneViewer);

                let snapshotUrlCat: string | undefined;
                try {
                    const imgCat = (sceneViewer as any)?.getSquareImageOfSite?.();
                    if (imgCat && typeof imgCat === 'string') snapshotUrlCat = imgCat;
                } catch {
                    snapshotUrlCat = undefined;
                }

                const liveCat = captureLiveLayoutCardMetrics();

                categoryVariants.push({
                    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    label: def.label,
                    roofIds: effectiveIds,
                    dir: def.dir,
                    minEff: def.minEff ?? 0,
                    productionW: liveCat.production,
                    modules: liveCat.modules,
                    efficiency: liveCat.efficiency,
                    solarAccess: liveCat.solarAccess,
                    multiplier: liveCat.multiplier,
                    snapshotUrl: snapshotUrlCat,
                });

                if (snapshotUrlCat) {
                    const snapshotKeyCat = JSON.stringify({
                        label: def.label,
                        roofIds: effectiveIds,
                        dir: def.dir ?? null,
                    });
                    useViewerStore.setState((prev: any) => ({
                        aiLayoutVariantSnapshots: {
                            ...(prev.aiLayoutVariantSnapshots || {}),
                            [snapshotKeyCat]: snapshotUrlCat,
                        },
                    }));
                }
            }
            nextCategoryVariants[categoryLabel] = categoryVariants;
        }

        useViewerStore.setState((prev: any) => ({
            ...prev,
            aiCategoryLayoutVariants: {
                ...(prev.aiCategoryLayoutVariants || {}),
                ...nextCategoryVariants,
            },
        }));

        try {
            await restoreDefaultLayoutFromSnapshot();
        } catch {
            // ignore restore errors
        }
    }

    syncPathwayMeshColorsFromPathActive(sceneViewer);

    return layoutVariantsSummary;
    } finally {
        await endBatchLayoutSiteCalc(sceneViewer);
    }
}
